import { useState, useLayoutEffect } from 'react';
import { PromiseState } from 'react-refetch';

const initialState = PromiseState.create();

type TCombinedFetchResult = [boolean, boolean, boolean, any, boolean, boolean];

/**
 * useCombinedRefetchState returns the combined state of all react-refetch fetches.
 * @method useCombinedRefetchState
 * @param  fetches  react-refetch instances of PromiseState (coming from your components' props)
 * @return An array of state booleans and optionally an Error object if the request fails
 */
function useCombinedRefetchState(
  fetches: PromiseState<any>[]
): TCombinedFetchResult {
  /** Create an empty PromiseState as the initial state */
  const state = PromiseState.all(fetches);

  const [fulfilled, setFulfilled] = useState<boolean>(initialState.fulfilled);
  const [pending, setPending] = useState<boolean>(initialState.pending);
  const [rejected, setRejected] = useState<boolean>(initialState.rejected);
  const [reason, setReason] = useState<any>(initialState.reason);
  const [settled, setSettled] = useState<boolean>(initialState.settled);
  const [refreshing, setRefreshing] = useState<boolean>(
    initialState.refreshing
  );

  useLayoutEffect(() => {
    setFulfilled(state.fulfilled);
    setPending(state.pending);
    setRejected(state.rejected);
    setReason(state.reason);
    setSettled(state.settled);
    setRefreshing(state.refreshing);
  }, [
    state.fulfilled,
    state.pending,
    state.reason,
    state.refreshing,
    state.rejected,
    state.settled,
  ]);

  return [fulfilled, pending, rejected, reason, settled, refreshing];
}

export default useCombinedRefetchState;
